import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { ModuleType, SmsDashboardTabEnums } from 'infrastructure/enums';
import { useInteractionActionButtons } from 'components/organisms/admin-control-panel/hooks/useInteractionActionButtons';
import ContactAvailability from 'components/molecules/smsDashboard/contactAvailability';
import GeneralCommentModalNew from 'components/templates/rpm/patients/patient/generalComments/modal-new';
import { usePatientContactAvailability } from 'infrastructure/hooks/patient/use-patient-contact-availability';
import BaseButton from 'components/atoms/baseButton';

import s from './styles.module.scss';
import SmsDashboardProfileInfoSkeleton from './components/skeleton';
import SmsDashboardProfileInfoDropDown from './components/dropdown';
import SmsDashboardPersonalInfo from './components/personal-info';
import MainInfo from './components/main-info';
import SmsDashboardContactInfo from './components/contact-info';
import InteractiveTime from './components/interactive-time';
import AccountInfo from './components/account-info';
import ClinicalNotesModal from './components/clinical-note-modal';

import type { RootState } from 'store';
import type { IPatientDetails } from 'infrastructure/hooks/smsDashboard/usePatientInfo';

interface VisibleProps {
  general: boolean;
  contact: boolean;
  availability: boolean;
  account: boolean;
  clinicalNotes: boolean;
}

interface Props {
  patientInfo: IPatientDetails | undefined;
  patientInfoLoading: boolean;
  fetchPatientInfo: () => void;
}

const ProfileInfo = ({
  patientInfo,
  patientInfoLoading,
  fetchPatientInfo,
}: Props) => {
  const { t } = useTranslation();
  const { currentConversation, conversationType } = useSelector(
    (state: RootState) => state.smsDashboard,
  );

  const [isVisible, setVisible] = useState<VisibleProps>({
    general: false,
    contact: false,
    availability: false,
    account: false,
    clinicalNotes: false,
  });

  const { patientGuid } = currentConversation.patient;

  const {
    commentsPopupOpen,
    commentLoading,
    setCommentsPopupOpen,
    sendComment,
  } = useInteractionActionButtons({ patientGuid });

  const { availabilities, loading: loadingAvailabilities } =
    usePatientContactAvailability({
      patientGuid,
    });

  useEffect(() => {
    const anchor = document.querySelector('#chat-profile__avatar');
    anchor?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [patientInfoLoading]);

  const toggleVisibility = async (name: keyof VisibleProps) => {
    setVisible({
      ...isVisible,
      [name]: !isVisible[name],
    });
  };

  return (
    <>
      {!patientInfoLoading ? (
        <div className={s.wrapper}>
          <MainInfo patientInfo={patientInfo} />
          <div className={s.controls}>
            <BaseButton
              className={s['general-comment']}
              label={t('smsDashboard.general_comment')}
              onClick={() => {
                fetchPatientInfo();
                setCommentsPopupOpen(true);
              }}
            />
            <BaseButton
              className={s['general-comment']}
              label={t('labels.clinicalNotesLogged')}
              onClick={() => {
                toggleVisibility('clinicalNotes');
              }}
            />
          </div>

          <SmsDashboardProfileInfoDropDown
            opened={isVisible.general}
            label={t('smsDashboard.personal_info')}
            onChange={() => toggleVisibility('general')}
          >
            <SmsDashboardPersonalInfo patientInfo={patientInfo} />
          </SmsDashboardProfileInfoDropDown>
          <SmsDashboardProfileInfoDropDown
            opened={isVisible.contact}
            label={t('smsDashboard.contact_info')}
            onChange={() => toggleVisibility('contact')}
          >
            <SmsDashboardContactInfo patientInfo={patientInfo} />
          </SmsDashboardProfileInfoDropDown>
          <SmsDashboardProfileInfoDropDown
            opened={isVisible.availability}
            label={t('smsDashboard.contact_availability')}
            onChange={() => toggleVisibility('availability')}
          >
            {!loadingAvailabilities && (
              <ContactAvailability
                availabilities={availabilities}
                timezone={patientInfo?.profile?.timezone!}
              />
            )}
          </SmsDashboardProfileInfoDropDown>
          <SmsDashboardProfileInfoDropDown
            opened={isVisible.account}
            label={t('smsDashboard.account_info')}
            onChange={() => toggleVisibility('account')}
          >
            <AccountInfo patientInfo={patientInfo} />
          </SmsDashboardProfileInfoDropDown>

          <div>
            <div className={s.title}>{t('smsDashboard.interactive_time')}</div>
            <InteractiveTime
              patientGuid={patientGuid}
              patientInfo={patientInfo}
            />
          </div>
          <ClinicalNotesModal
            isOpen={isVisible.clinicalNotes}
            patientGuid={patientGuid}
            onCancel={() => toggleVisibility('clinicalNotes')}
          />
          <GeneralCommentModalNew
            isOpen={commentsPopupOpen}
            toggle={setCommentsPopupOpen}
            patientGuid={patientGuid}
            loading={commentLoading}
            submit={sendComment}
            module={
              conversationType === SmsDashboardTabEnums.RPM
                ? ModuleType.RPM
                : ModuleType.CCM
            }
            interactionStatus={
              conversationType === SmsDashboardTabEnums.RPM
                ? patientInfo?.profile.interactionStatus
                : patientInfo?.profile.ccmInteractionStatus
            }
          />
        </div>
      ) : (
        <SmsDashboardProfileInfoSkeleton />
      )}
    </>
  );
};

export default ProfileInfo;
